import { useState } from 'react';
import { APCalculusLesson } from '../data';

interface LessonCardProps {
  lesson: APCalculusLesson;
}

export default function LessonCard({ lesson }: LessonCardProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'practice'>('overview');
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});
  const [showExplanations, setShowExplanations] = useState<Record<number, boolean>>({});
  const [filterStatus, setFilterStatus] = useState<'all' | 'correct' | 'incorrect'>('all');
  const [hoveredTerm, setHoveredTerm] = useState<string | null>(null);

  const handleAnswerSelect = (questionId: number, answerIndex: number) => {
    if (!showResults) {
      setSelectedAnswers(prev => ({
        ...prev,
        [questionId]: answerIndex
      }));
    }
  };

  const handleSubmit = () => {
    setShowResults(true);
  };

  const handleReset = () => {
    setSelectedAnswers({});
    setShowResults(false);
    setShowExplanations({});
    setFilterStatus('all');
  };

  const toggleStep = (index: number) => {
    setExpandedSteps(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleExplanation = (questionId: number) => {
    setShowExplanations(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const calculateScore = () => {
    let correct = 0;
    lesson.questions.forEach(q => {
      if (selectedAnswers[q.id] === q.correctAnswer) {
        correct++;
      }
    });
    return {
      correct,
      total: lesson.questions.length,
      percentage: Math.round((correct / lesson.questions.length) * 100)
    };
  };

  const getFilteredQuestions = () => {
    if (!showResults || filterStatus === 'all') {
      return lesson.questions;
    }
    return lesson.questions.filter(q => {
      const isCorrect = selectedAnswers[q.id] === q.correctAnswer;
      return filterStatus === 'correct' ? isCorrect : !isCorrect;
    });
  };

  const score = showResults ? calculateScore() : null;

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow">
      {/* Card Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-sm font-medium text-blue-100 mb-2">{lesson.unit}</div>
            <h3 className="text-2xl font-bold mb-3">{lesson.title}</h3>
            <p className="text-blue-50 leading-relaxed">{lesson.description}</p>
          </div>
          <div className="w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm flex-shrink-0">
            <i className="ri-function-line text-3xl"></i>
          </div>
        </div>

        {/* Standards */}
        <div className="flex flex-wrap gap-2 mt-4">
          {lesson.standards.map((standard, idx) => (
            <span key={idx} className="px-3 py-1 bg-white/20 rounded-full text-xs font-medium backdrop-blur-sm">
              {standard}
            </span>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 px-6 py-4 font-semibold transition-colors ${
              activeTab === 'overview'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <i className="ri-book-open-line mr-2"></i>
            Lesson Overview
          </button>
          <button
            onClick={() => setActiveTab('practice')}
            className={`flex-1 px-6 py-4 font-semibold transition-colors ${
              activeTab === 'practice'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <i className="ri-questionnaire-line mr-2"></i>
            Practice (30 Questions)
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' ? (
          <div className="space-y-8">
            {/* Vocabulary */}
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-book-2-line text-blue-600"></i>
                Key Vocabulary
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                {lesson.vocabulary.map((item, idx) => (
                  <div
                    key={idx}
                    className="relative p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all cursor-help"
                    onMouseEnter={() => setHoveredTerm(item.term)}
                    onMouseLeave={() => setHoveredTerm(null)}
                  >
                    <div className="font-semibold text-gray-900">{item.term}</div>
                    {hoveredTerm === item.term && (
                      <div className="absolute z-10 left-0 right-0 top-full mt-2 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-xl">
                        {item.definition}
                        <div className="absolute -top-2 left-4 w-4 h-4 bg-gray-900 transform rotate-45"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Concept Explanation */}
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-lightbulb-line text-yellow-600"></i>
                Concept Explanation
              </h4>
              <div className="prose max-w-none">
                {lesson.conceptExplanation.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-gray-700 leading-relaxed mb-4 whitespace-pre-wrap">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Worked Example */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
              <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-file-edit-line text-purple-600"></i>
                Worked Example
              </h4>
              
              <div className="bg-white rounded-lg p-4 mb-4 border border-purple-200">
                <div className="font-semibold text-gray-900 mb-2">Problem:</div>
                <div className="text-gray-700 whitespace-pre-wrap">{lesson.workedExample.problem}</div>
              </div>

              <div className="space-y-3">
                {lesson.workedExample.steps.map((step, idx) => (
                  <div key={idx} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleStep(idx)}
                      className="w-full px-4 py-3 text-left font-semibold text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>{step.step}</span>
                      <i className={`ri-arrow-${expandedSteps[idx] ? 'up' : 'down'}-s-line text-xl`}></i>
                    </button>
                    {expandedSteps[idx] && (
                      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                        <div className="text-gray-700 whitespace-pre-wrap font-mono text-sm">{step.work}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Teacher Tip */}
            <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
              <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-user-star-line text-amber-600"></i>
                Mr. Augustine's Teaching Tip
              </h4>
              <p className="text-gray-700 leading-relaxed italic">{lesson.teacherTip}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Practice Controls */}
            <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-4">
                <span className="text-gray-700 font-medium">
                  {Object.keys(selectedAnswers).length} / {lesson.questions.length} answered
                </span>
                {showResults && (
                  <>
                    <div className="h-6 w-px bg-gray-300"></div>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setFilterStatus('all')}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          filterStatus === 'all'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        All
                      </button>
                      <button
                        onClick={() => setFilterStatus('correct')}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          filterStatus === 'correct'
                            ? 'bg-green-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Correct
                      </button>
                      <button
                        onClick={() => setFilterStatus('incorrect')}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          filterStatus === 'incorrect'
                            ? 'bg-red-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Incorrect
                      </button>
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                {!showResults ? (
                  <button
                    onClick={handleSubmit}
                    disabled={Object.keys(selectedAnswers).length === 0}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                  >
                    Submit & Score
                  </button>
                ) : (
                  <button
                    onClick={handleReset}
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors whitespace-nowrap"
                  >
                    Reset Quiz
                  </button>
                )}
              </div>
            </div>

            {/* Score Display */}
            {showResults && score && (
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-blue-100 mb-1">Your Score</div>
                    <div className="text-4xl font-bold">{score.correct} / {score.total}</div>
                    <div className="text-blue-100 mt-1">{score.percentage}% Correct</div>
                  </div>
                  <div className="text-6xl">
                    {score.percentage >= 90 ? '🎉' : score.percentage >= 70 ? '👍' : '📚'}
                  </div>
                </div>
                {score.percentage >= 90 && (
                  <div className="mt-4 text-blue-100">
                    Outstanding! You've mastered this lesson!
                  </div>
                )}
                {score.percentage >= 70 && score.percentage < 90 && (
                  <div className="mt-4 text-blue-100">
                    Good job! Review the missed questions to improve.
                  </div>
                )}
                {score.percentage < 70 && (
                  <div className="mt-4 text-blue-100">
                    Keep practicing! Review the lesson overview and try again.
                  </div>
                )}
              </div>
            )}

            {/* Progress Bar */}
            {!showResults && (
              <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-full transition-all duration-300"
                  style={{ width: `${(Object.keys(selectedAnswers).length / lesson.questions.length) * 100}%` }}
                ></div>
              </div>
            )}

            {/* Questions */}
            <div className="space-y-6">
              {getFilteredQuestions().map((question) => {
                const isAnswered = selectedAnswers[question.id] !== undefined;
                const isCorrect = selectedAnswers[question.id] === question.correctAnswer;
                
                return (
                  <div
                    key={question.id}
                    className={`p-6 rounded-lg border-2 transition-all ${
                      showResults
                        ? isCorrect
                          ? 'border-green-500 bg-green-50'
                          : isAnswered
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 bg-white'
                        : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                        {question.id}
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium leading-relaxed">{question.question}</p>
                      </div>
                      {showResults && (
                        <div className="flex-shrink-0">
                          {isCorrect ? (
                            <i className="ri-check-line text-2xl text-green-600"></i>
                          ) : isAnswered ? (
                            <i className="ri-close-line text-2xl text-red-600"></i>
                          ) : (
                            <i className="ri-subtract-line text-2xl text-gray-400"></i>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 ml-12">
                      {question.options.map((option, optionIdx) => {
                        const isSelected = selectedAnswers[question.id] === optionIdx;
                        const isCorrectOption = optionIdx === question.correctAnswer;
                        
                        return (
                          <button
                            key={optionIdx}
                            onClick={() => handleAnswerSelect(question.id, optionIdx)}
                            disabled={showResults}
                            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                              showResults
                                ? isCorrectOption
                                  ? 'border-green-500 bg-green-100 font-semibold'
                                  : isSelected
                                  ? 'border-red-500 bg-red-100'
                                  : 'border-gray-200 bg-gray-50'
                                : isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                            } ${showResults ? 'cursor-default' : 'cursor-pointer'}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                showResults
                                  ? isCorrectOption
                                    ? 'border-green-600 bg-green-600'
                                    : isSelected
                                    ? 'border-red-600 bg-red-600'
                                    : 'border-gray-300'
                                  : isSelected
                                  ? 'border-blue-600 bg-blue-600'
                                  : 'border-gray-300'
                              }`}>
                                {((showResults && isCorrectOption) || (!showResults && isSelected)) && (
                                  <i className="ri-check-line text-white text-sm"></i>
                                )}
                              </div>
                              <span className="text-gray-900">{option}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {showResults && (
                      <div className="ml-12 mt-4">
                        <button
                          onClick={() => toggleExplanation(question.id)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <i className={`ri-arrow-${showExplanations[question.id] ? 'up' : 'down'}-s-line`}></i>
                          {showExplanations[question.id] ? 'Hide' : 'Show'} Explanation
                        </button>
                        {showExplanations[question.id] && (
                          <div className="mt-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-gray-700 leading-relaxed">{question.explanation}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}